import {inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of} from 'rxjs';
import {environment} from '../../../environments/environment';
import {NotificationView} from '../../../models/generated-models';

/** In-app notification list + unread count for the header bell dropdown. */
@Injectable({providedIn: 'root'})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.serverUrl;

  readonly unreadCount = signal(0);
  readonly items = signal<NotificationView[]>([]);

  /** Cheap poll (reused by the existing 10s heartbeat) — just the unread badge count. */
  refreshUnreadCount(): Observable<void> {
    return this.http.get<number>(`${this.baseUrl}/api/notifications/unread-count`).pipe(
      map((count) => this.unreadCount.set(count ?? 0)),
      catchError(() => {
        this.unreadCount.set(0);
        return of(undefined);
      }),
    );
  }

  /** Load the latest notifications (called when the dropdown opens). */
  load(limit = 20): void {
    this.http.get<NotificationView[]>(`${this.baseUrl}/api/notifications`, {params: {limit}}).subscribe({
      next: (items) => this.items.set(items ?? []),
      error: () => this.items.set([]),
    });
  }

  markRead(id: number): void {
    this.http.post<void>(`${this.baseUrl}/api/notifications/${id}/read`, null).subscribe({
      next: () => {
        this.items.update((list) => list.map((n) => (n.id === id ? {...n, read: true} : n)));
        this.unreadCount.update((c) => Math.max(0, c - 1));
      },
      error: () => {
      },
    });
  }

  markAllRead(): void {
    this.http.post<void>(`${this.baseUrl}/api/notifications/mark-all-read`, null).subscribe({
      next: () => {
        this.items.update((list) => list.map((n) => ({...n, read: true})));
        this.unreadCount.set(0);
      },
      error: () => {
      },
    });
  }
}
